import type { Escaner } from "./port";
import { openEnvironmentCamera, stopCameraStream } from "./cameraStream";

// ADR-012: adaptador sobre la API nativa del navegador (`BarcodeDetector` +
// `getUserMedia`). No importa ninguna librería de decodificación: es
// justamente el camino que no necesita una, y por eso Chrome en Android (que
// sí trae `BarcodeDetector`) nunca descarga el WASM de respaldo.
interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
}

function getBarcodeDetectorConstructor(): BarcodeDetectorConstructor | null {
  const ctor = (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector;
  return typeof ctor === "function"
    ? (ctor as unknown as BarcodeDetectorConstructor)
    : null;
}

const SCAN_INTERVAL_MS = 200;

export function createNativeScanner(): Escaner {
  let stream: MediaStream | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let detecting = false;

  return {
    disponible(): boolean {
      return getBarcodeDetectorConstructor() !== null;
    },
    async iniciar(video, alDetectar) {
      const BarcodeDetectorCtor = getBarcodeDetectorConstructor();
      if (!BarcodeDetectorCtor) {
        throw new Error("BarcodeDetector no disponible");
      }
      const detector = new BarcodeDetectorCtor({ formats: ["qr_code"] });

      stream = await openEnvironmentCamera(video);

      intervalId = setInterval(() => {
        if (detecting) return;
        detecting = true;
        detector
          .detect(video)
          .then((codes) => {
            const first = codes[0];
            if (first) {
              alDetectar(first.rawValue);
            }
          })
          .catch(() => {
            // Un frame ilegible no es un error: se reintenta en el
            // próximo ciclo.
          })
          .finally(() => {
            detecting = false;
          });
      }, SCAN_INTERVAL_MS);
    },
    detener() {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      stopCameraStream(stream);
      stream = null;
    },
  };
}
